import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {
  alpha,
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import I18n from "i18n";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

export type NestedSidebarItem = {
  id: string;
  label: string;
  icon?: ReactNode;
  badge?: ReactNode;
  path?: string;
  exact?: boolean;
  visible?: boolean;
  children?: NestedSidebarItem[];
};

export interface NestedSidebarNavigationProps {
  items: NestedSidebarItem[];
  expanded: boolean;
  onRequestExpand?: () => void;
  onNavigate?: () => void;
  ariaLabel?: string;
}

function normalizePath(path: string) {
  if (path === "/") {
    return path;
  }
  return path.replace(/\/+$/, "");
}

function isLeafActive(
  pathname: string,
  item: Pick<NestedSidebarItem, "path" | "exact">,
) {
  if (!item.path) {
    return false;
  }

  const currentPath = normalizePath(pathname);
  const itemPath = normalizePath(item.path);
  return item.exact
    ? currentPath === itemPath
    : currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
}

function hasActiveDescendant(
  pathname: string,
  item: NestedSidebarItem,
): boolean {
  if (isLeafActive(pathname, item)) {
    return true;
  }
  return !!item.children?.some(child => hasActiveDescendant(pathname, child));
}

function getActiveParentIds(
  pathname: string,
  items: NestedSidebarItem[],
  parentIds: string[] = [],
): string[] {
  return items.reduce<string[]>((ids, item) => {
    if (!item.children?.length) {
      return ids;
    }

    const nextParentIds = [...parentIds, item.id];
    if (item.children.some(child => hasActiveDescendant(pathname, child))) {
      ids.push(...nextParentIds);
    }
    ids.push(...getActiveParentIds(pathname, item.children, nextParentIds));
    return ids;
  }, []);
}

function filterVisibleItems(items: NestedSidebarItem[]): NestedSidebarItem[] {
  return items.reduce<NestedSidebarItem[]>((visibleItems, item) => {
    if (item.visible === false) {
      return visibleItems;
    }

    const children = item.children
      ? filterVisibleItems(item.children)
      : undefined;
    if (item.children && !children?.length) {
      return visibleItems;
    }

    visibleItems.push({ ...item, children });
    return visibleItems;
  }, []);
}

export default function NestedSidebarNavigation({
  items,
  expanded,
  onRequestExpand,
  onNavigate,
  ariaLabel = I18n.t("Sidebar.mainNavigation"),
}: NestedSidebarNavigationProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { pathname } = useLocation();
  const visibleItems = useMemo(() => filterVisibleItems(items), [items]);
  const activeParentIds = useMemo(
    () => new Set(getActiveParentIds(pathname, visibleItems)),
    [pathname, visibleItems],
  );
  const activeParentKey = Array.from(activeParentIds).sort().join("|");
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [tooltipItemId, setTooltipItemId] = useState<string>();

  useEffect(() => {
    setOpenItems(current => {
      const next = { ...current };
      activeParentIds.forEach(id => {
        next[id] = true;
      });
      return next;
    });
  }, [activeParentKey]);

  useEffect(() => {
    if (expanded || !isDesktop) {
      setTooltipItemId(undefined);
    }
  }, [expanded, isDesktop]);

  const toggleItem = (id: string) => {
    setTooltipItemId(undefined);
    if (!expanded) {
      onRequestExpand?.();
      return;
    }
    setOpenItems(current => ({ ...current, [id]: !current[id] }));
  };

  const renderItem = (item: NestedSidebarItem, depth: number): ReactNode => {
    const hasChildren = !!item.children?.length;
    const active = hasActiveDescendant(pathname, item);
    const open =
      expanded && (activeParentIds.has(item.id) || !!openItems[item.id]);
    const content = (
      <>
        {!!item.icon && (
          <ListItemIcon
            sx={{
              minWidth: expanded ? 36 : 0,
              color: "inherit",
              justifyContent: "center",
            }}
          >
            {item.icon}
          </ListItemIcon>
        )}
        <ListItemText
          primary={item.label}
          sx={{
            display: expanded ? "block" : "none",
            my: 0,
            minWidth: 0,
            "& .MuiListItemText-primary": {
              fontSize: depth === 0 ? "0.875rem" : "0.825rem",
              fontWeight: active ? 700 : depth === 0 ? 600 : 500,
              whiteSpace: "normal",
              lineHeight: 1.25,
            },
          }}
        />
        {expanded && item.badge}
        {expanded && hasChildren && (
          <ChevronRightRoundedIcon
            fontSize="small"
            sx={{
              ml: 0.5,
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
              transition: theme =>
                theme.transitions.create("transform", {
                  duration: theme.transitions.duration.shortest,
                }),
            }}
          />
        )}
      </>
    );

    const button = (
      <ListItemButton
        component={!hasChildren && item.path ? Link : "button"}
        to={!hasChildren && item.path ? item.path : undefined}
        selected={!hasChildren && active}
        aria-current={!hasChildren && active ? "page" : undefined}
        aria-expanded={hasChildren ? open : undefined}
        aria-controls={hasChildren ? `sidebar-group-${item.id}` : undefined}
        aria-label={!expanded ? item.label : undefined}
        onClick={hasChildren ? () => toggleItem(item.id) : onNavigate}
        onMouseEnter={() => {
          if (!expanded && isDesktop) {
            setTooltipItemId(item.id);
          }
        }}
        onMouseLeave={() => {
          setTooltipItemId(current =>
            current === item.id ? undefined : current,
          );
        }}
        sx={{
          minHeight: 44,
          mx: expanded ? 1 : 0.75,
          my: 0.25,
          px: expanded ? 1.5 : 0,
          pl: expanded ? 1.5 + depth * 1.5 : 0,
          borderRadius: 1.5,
          justifyContent: expanded ? "flex-start" : "center",
          color: theme =>
            alpha(
              theme.palette.getContrastText(theme.palette.primary.main),
              0.8,
            ),
          "&:hover": {
            color: "common.white",
            backgroundColor: theme => alpha(theme.palette.common.white, 0.1),
          },
          "&.Mui-selected": {
            color: "common.white",
            backgroundColor: theme => alpha(theme.palette.common.white, 0.18),
            "&:hover": {
              backgroundColor: theme => alpha(theme.palette.common.white, 0.24),
            },
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 8,
              bottom: 8,
              width: 3,
              borderRadius: "0 3px 3px 0",
              backgroundColor: "common.white",
            },
          },
        }}
      >
        {content}
      </ListItemButton>
    );

    return (
      <Box component="li" key={item.id} sx={{ display: "block" }}>
        <Tooltip
          title={item.label}
          placement="right"
          open={!expanded && isDesktop && tooltipItemId === item.id}
          disableHoverListener
          disableFocusListener
          disableTouchListener
        >
          {button}
        </Tooltip>
        {hasChildren && (
          <Collapse in={open} timeout={180} unmountOnExit>
            <List
              id={`sidebar-group-${item.id}`}
              component="ul"
              disablePadding
              aria-label={item.label}
            >
              {item.children!.map(child => renderItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Box component="nav" aria-label={ariaLabel} sx={{ width: "100%" }}>
      <List component="ul" disablePadding>
        {visibleItems.map(item => renderItem(item, 0))}
      </List>
    </Box>
  );
}
